import { useState, useEffect } from 'react';
import { validateAmount, formatNumber, hasTooManyDecimals } from '../helpers/functions';
import CONTENT_STRINGS from '../helpers/contentStrings';

export const useDecimalErrors = () => {
    const [errors, setErrors] = useState({});

    const checkDecimal = (key, val) => {
        const tooMany = hasTooManyDecimals(val);
        setErrors(prev => ({ ...prev, [key]: tooMany ? CONTENT_STRINGS.TOO_MANY_DECIMALS : '' }));
        return tooMany;
    };

    const clearAll = () => setErrors({});

    const hasErrors = Object.values(errors).some(Boolean);

    return { errors, checkDecimal, clearAll, hasErrors };
};

const useAmountField = ({ name, value, minimum, onChange, enabled = true }) => {
    const [error, setError] = useState('');
    const [decimalError, setDecimalError] = useState('');

    // Deliberately keyed on `name` only, not `value` — `value` changes on every keystroke,
    // and formatNumber() reformats mid-typing (e.g. "5." -> "5", "5.5" -> "5.50"), which
    // would fight the user's own typing on every character. This runs once per field
    // (e.g. to normalize a value that arrived pre-formatted before the field existed);
    // format-on-completion is `onBlur` below, not this effect.
    useEffect(() => {
        if (enabled && value) {
            const formatted = formatNumber(value);
            if (formatted !== value) onChange(name, formatted);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name]);

    const validate = (val) => {
        const err = validateAmount(val, minimum ?? null);
        setError(err);
        return err;
    };

    const onBlur = () => {
        const err = validate(value);
        if (!err && value) onChange(name, formatNumber(value));
    };

    const checkDecimal = (val) => {
        const tooMany = hasTooManyDecimals(val);
        setDecimalError(tooMany ? CONTENT_STRINGS.TOO_MANY_DECIMALS : '');
        return tooMany;
    };

    return { error, validate, onBlur, decimalError, checkDecimal };
};

export default useAmountField;
